import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ThemeType } from '../../types/command';
import { themeTokens } from '../../styles/theme';

interface SectionWrapperStyleProps {
  $themeName: ThemeType;
  $visible: boolean;
}

const StyledSection = styled.section<SectionWrapperStyleProps>`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 32px auto;
  padding: 48px 32px;
  border-radius: 10px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].cardBg};
  scroll-margin-top: 80px;

  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '30px')});
  transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }

  @media (max-width: 768px) {
    padding: 32px 20px;
  }

  @media (max-width: 480px) {
    padding: 24px 16px;
    margin-bottom: 24px;
  }
`;

interface SectionWrapperProps {
  id: string;
  themeName: ThemeType;
  children: React.ReactNode;
  className?: string;
}

const SectionWrapper: React.FC<SectionWrapperProps> = ({ id, themeName, children, className }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <StyledSection id={id} ref={ref} $themeName={themeName} $visible={visible} className={className}>
      {children}
    </StyledSection>
  );
};

export default SectionWrapper;
